import React, { useRef } from "react";
import gallery from "../../../assets/gallery.svg";
import { useToast } from "../../../utils/toast";
import "../../../utils/toast.css";

/**
 * GallerySelect component - Handles image selection from device gallery
 *
 * Features:
 * - Opens native file picker for image selection
 * - Validates file type to ensure only images are processed
 * - Enforces upload limits based on remaining capacity
 * - Converts selected images to canvas for consistent handling
 * - Supports multiple image selection with validation
 * - Cleans up memory by revoking object URLs
 * - Provides user feedback for invalid files and limit violations
 *
 * Props:
 * @param {Function} addImage - Function to add processed images to state
 * @param {number} remainingUploads - Number of uploads remaining for current event
 * @param {number} totalUploads - Total uploads allowed for current event
 */
const GallerySelect = ({
  addImage, // Function to add processed images to the application state
  remainingUploads, // Number of uploads remaining for current event
  totalUploads, // Total uploads allowed for current event
  buttonLabel,
}) => {
  // Reference to hidden file input element for programmatic access
  const fileInputRef = useRef(null);
  const { showToast } = useToast(); // Toast notification hook

  /**
   * Opens the native file picker dialog
   * Programmatically clicks the hidden file input
   */
  const handleGalleryClick = () => fileInputRef.current.click();

  /**
   * Processes selected image files from file input
   * Validates file types, upload limits, and converts images to canvas
   * @param {Event} e - File input change event containing selected files
   */
  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    addImage(null);
    // Validate against remaining upload capacity
    // Prevents users from selecting more images than they can upload
    if (files.length > remainingUploads) {
      showToast(
        `You can only select up to ${remainingUploads} ${totalUploads === remainingUploads ? "" : "more"} image${remainingUploads === 1 ? "" : "s"}.`,
        'warning'
      );
      e.target.value = ""; // Clear the input to allow re-selection
      return;
    }

    // Process each selected file individually
    files.forEach((file) => {
      // Validate file type - only allow image files
      if (!file.type.startsWith("image/")) {
        showToast("Only image files are allowed", 'error');
        return;
      }

      // Create image object for file processing
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);
      img.src = objectUrl;

      // Handle successful image loading
      img.onload = () => {
        // Use offscreen canvas for safe image processing (doesn't affect DOM)
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;

        // Draw image to canvas for processing
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);

        // Convert canvas to data URL for storage
        const dataUrl = canvas.toDataURL("image/png");

        // Add processed image to application state
        addImage({
          original: dataUrl, // Unfiltered version
          filtered: dataUrl, // Filtered version (same initially)
        });

        // Clean up memory by revoking object URL
        URL.revokeObjectURL(objectUrl);
      };

      // Handle image loading errors
      img.onerror = () => {
        URL.revokeObjectURL(objectUrl); // Clean up on error
        showToast("Failed to load image", 'error');
      };
    });

    // Reset input value so same files can be selected again
    e.target.value = "";
  };

  return (
    <>
      {/* 
        Hidden file input element
        - accept="image/*" restricts to image files only
        - multiple attribute allows selecting multiple files
        - Hidden from UI, triggered programmatically via handleGalleryClick
        - onChange event handler processes selected files
      */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        hidden
        onChange={handleImageSelect}
      />

      {/* 
        Gallery button UI element
        - Styled with CSS class .gallery-btn
        - Shows gallery icon for visual recognition
        - Triggers file picker on click via handleGalleryClick
        - Provides accessible alternative to hidden file input
      */}
      <button type="button" onClick={handleGalleryClick} className={buttonLabel ? "camera-error-btn" : "gallery-btn"}>
        {buttonLabel || <img src={gallery} alt="Gallery" />}
      </button>
    </>
  );
};

export default GallerySelect;
